import React, { useState, useEffect } from 'react';
import { ArrowLeft, List, Send } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { t } from '../../i18n';
import { AppFooter } from '../Common/AppFooter';
import { InquiryForm } from '../MyPage/InquiryForm';
import { InquiryList } from '../MyPage/InquiryList';
import SEO from '../Common/SEO';

interface HelpViewProps {
    onBack: () => void;
}

type HelpTab = 'form' | 'list';

export const HelpView: React.FC<HelpViewProps> = ({ onBack }) => {
    const { lang } = useLanguage();
    const [activeTab, setActiveTab] = useState<HelpTab>('form');
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // PullToRefresh 내부의 스크롤 컨테이너를 찾아 초기화
        const scrollContainers = document.querySelectorAll('.overflow-y-auto');
        scrollContainers.forEach(container => {
            container.scrollTop = 0;
        });
    }, []);

    const seoTitle = activeTab === 'form' 
        ? t(lang, 'contactSupport')
        : t(lang, 'mySupportHistory');
    
    const seoDesc = activeTab === 'form'
        ? t(lang, 'contactSupportDesc')
        : t(lang, 'mySupportHistoryDesc');

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <SEO title={seoTitle} description={seoDesc} />
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 px-6 py-4 flex items-center gap-4">
                <button onClick={onBack} className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full text-slate-600 active:scale-90 transition-all md:hover:bg-rose-50 md:hover:text-rose-500">
                    <ArrowLeft size={20} />
                </button>
                <h1 className="text-xl font-black text-slate-900 tracking-tight uppercase">{t(lang, 'helpCenter')}</h1>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-8 md:py-12 custom-scrollbar">
                <div className="max-w-2xl mx-auto space-y-8">
                    {/* Tab Selection */}
                    <div className="flex p-1.5 bg-slate-100 rounded-[24px] gap-1">
                        <button 
                            onClick={() => setActiveTab('form')}
                            className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-[20px] text-xs font-black uppercase tracking-widest transition-all ${
                                activeTab === 'form' 
                                    ? 'bg-white text-slate-900 shadow-sm' 
                                    : 'text-slate-400 md:hover:text-slate-600'
                            }`}
                        >
                            <Send size={16} />
                            {t(lang, 'newInquiry')}
                        </button>
                        <button 
                            onClick={() => setActiveTab('list')}
                            className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-[20px] text-xs font-black uppercase tracking-widest transition-all ${
                                activeTab === 'list' 
                                    ? 'bg-white text-slate-900 shadow-sm' 
                                    : 'text-slate-400 md:hover:text-slate-600'
                            }`}
                        >
                            <List size={16} />
                            {t(lang, 'myHistory')}
                        </button>
                    </div>

                    <div className="min-h-[400px]">
                        {activeTab === 'form' ? (
                            <div className="space-y-8">
                                <InquiryForm 
                                    lang={lang} 
                                    onSuccess={() => {
                                        setRefreshTrigger(prev => prev + 1);
                                        setActiveTab('list');
                                    }} 
                                />
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <div className="flex items-center justify-between px-2">
                                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">
                                        {t(lang, 'myInquiryHistory')}
                                    </h3>
                                    <span className="text-[10px] font-black text-rose-500 uppercase tracking-widest">
                                        Support History
                                    </span>
                                </div>
                                <InquiryList lang={lang} refreshTrigger={refreshTrigger} />
                            </div>
                        )}
                    </div>

                    <div className="text-center py-10 border-t border-slate-100">

                        <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em] mb-4">
                            {t(lang, 'emailSupport')}
                        </p>
                        <a href="mailto:support@xleven.co" className="text-slate-900 font-black text-sm md:hover:text-rose-500 transition-colors">
                            support@xleven.co
                        </a>
                    </div>
                </div>
            </div>
            <AppFooter lang={lang} />
        </div>
    );
};
